import React from 'react';
import { Helmet } from 'react-helmet';
import { SEO_DEFAULTS } from 'constants/SeoConfig';

/**
 * Reusable SEO Helmet wrapper component.
 * Renders <title>, meta description, robots, canonical, Open Graph, and Twitter Card tags.
 *
 * @param {string} title - Page title
 * @param {string} description - Meta description
 * @param {string} [canonical] - Canonical URL
 * @param {string} [robots='index, follow'] - Robots directive
 * @param {string} [ogImage] - Open Graph image URL (defaults to site logo)
 * @param {string} [ogType='website'] - Open Graph type
 */
const PageSEO = ({
    title,
    description,
    canonical,
    robots = SEO_DEFAULTS.robots,
    ogImage = SEO_DEFAULTS.ogImage,
    ogType = 'website',
}) => {
    if (!title) return null;

    // Sanitize canonical: collapse double slashes (except after protocol), strip trailing slash
    const cleanCanonical = canonical
        ? canonical.replace(/([^:])\/\/+/g, '$1/').replace(/\/$/, '')
        : null;

    return (
        <Helmet>
            <title>{title}</title>
            {description && <meta name="description" content={description} />}
            <meta name="robots" content={robots} />
            {cleanCanonical && <link rel="canonical" href={cleanCanonical} />}

            {/* Open Graph */}
            <meta property="og:title" content={title} />
            {description && <meta property="og:description" content={description} />}
            {cleanCanonical && <meta property="og:url" content={cleanCanonical} />}
            <meta property="og:image" content={ogImage} />
            <meta property="og:type" content={ogType} />
            <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />

            {/* Twitter Card */}
            <meta name="twitter:card" content={SEO_DEFAULTS.twitterCard} />
            <meta name="twitter:title" content={title} />
            {description && <meta name="twitter:description" content={description} />}
            <meta name="twitter:image" content={ogImage} />
        </Helmet>
    );
};

export default PageSEO;
